import { tokenStorage } from "@/auth/storage";
import type {
  ApiError,
  HealthCheck,
  MeResponse,
  RedeemInviteResponse,
  TenantAccess,
} from "./generated";

/**
 * Error thrown by `apiFetch` for non-2xx responses.
 *
 * Always a real `Error` — never the bare `ApiError` body. Rejecting with a
 * plain object made Solid's `createResource` surface it as an unhandled
 * "Error: Unknown error" with no diagnostic value (Z01-WEB-1). `message` is
 * the server's `ApiError.message` when present, so existing catch-sites that
 * read `.message` keep working, and `instanceof Error` sites now match.
 */
export class ApiRequestError extends Error {
  readonly code?: ApiError["code"];
  readonly detail?: unknown;
  readonly requestId?: string;

  constructor(
    readonly status: number,
    statusText: string,
    body?: ApiError,
  ) {
    super(body?.message ?? `HTTP ${status} ${statusText}`);
    this.name = "ApiRequestError";
    this.code = body?.code;
    this.detail = body?.detail;
    this.requestId = body?.request_id;
  }
}

/**
 * Parse the error body (if any) off a non-2xx response and throw the
 * corresponding [`ApiRequestError`]. Shared by `apiFetch` and the manual
 * fetch helpers (DELETE/upload paths that bypass `apiFetch` for
 * 204-no-body responses) so every API failure has the same error shape.
 */
export async function throwApiRequestError(res: Response): Promise<never> {
  let body: ApiError | undefined;
  try {
    body = (await res.json()) as ApiError;
  } catch {
    // fall through
  }
  throw new ApiRequestError(res.status, res.statusText, body);
}

/**
 * Typed fetch wrapper.
 *
 * Throws an [`ApiRequestError`] on non-2xx (carrying the parsed `ApiError`
 * body fields when the server provided them). Callers should wrap with
 * try/catch where they want UI affordances. Bearer header is auto-injected
 * when a token is present in sessionStorage; protected handlers will 401
 * when the token is absent or stale.
 */
export async function apiFetch<T>(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...((init?.headers as Record<string, string>) ?? {}),
  };
  const tokens = tokenStorage.load();
  if (tokens?.access_token && !("Authorization" in headers)) {
    headers.Authorization = `Bearer ${tokens.access_token}`;
  }

  const res = await fetch(input, { ...init, headers });

  if (!res.ok) {
    await throwApiRequestError(res);
  }

  return (await res.json()) as T;
}

export function fetchHealth(): Promise<HealthCheck> {
  return apiFetch<HealthCheck>("/api/healthz");
}

/**
 * `/readyz` returns 503 with the same HealthCheck body when a dependency
 * is down (OPS-2), so parse the body regardless of HTTP status instead of
 * throwing like `apiFetch` would. An unreachable/garbled API degrades to a
 * synthetic "down" so dashboard resources never enter an error state.
 */
export async function fetchReady(): Promise<HealthCheck> {
  try {
    const res = await fetch("/api/readyz", {
      headers: { Accept: "application/json" },
    });
    return (await res.json()) as HealthCheck;
  } catch {
    return {
      status: "down",
      version: "unknown",
      now: new Date().toISOString(),
      dependencies: {},
    };
  }
}

export function fetchMe(): Promise<MeResponse> {
  return apiFetch<MeResponse>("/api/v1/me");
}

export function fetchAccess(): Promise<TenantAccess> {
  return apiFetch<TenantAccess>("/api/v1/access");
}

export function redeemInvite(code: string): Promise<RedeemInviteResponse> {
  return apiFetch<RedeemInviteResponse>("/api/v1/invites/redeem", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
}

export function generateImage(
  prompt: string,
  conversationId?: string,
): Promise<{ markdown: string }> {
  return apiFetch<{ markdown: string }>("/api/v1/chat/generate-image", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ prompt, conversation_id: conversationId }),
  });
}
